import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { andList, placeWords } from "../../../shared/goals/audience";
import type { GoalConfig } from "../../../shared/goals/config";
import { metaSettings } from "../../../shared/goals/meta";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { PnRow, Segmented } from "../../../shared/phone/Row";
import { ActionSheet, Sheet } from "../../../shared/phone/Sheet";
import { useRail } from "../../../shared/phone/media";
import { Icon } from "../../components/ui/Icon";
import { ccApi, type ReadyStep } from "../../lib/ccApi";
import { connectionReady, emailOf, failing, hideProvider, metaOf, shownError, shownState, type Connection } from "../../lib/connectionsApi";
import type { GoalView } from "../../lib/goalsApi";
import { drafting, readyApi, stopped, type LaunchView, type ReadyEmail, type ReadyList, type ReadyView } from "../../lib/readyApi";
import { BudgetSheet } from "../ads/BudgetSheet";
import { LaunchFoot } from "../ads/LaunchFoot";
import { NativeToast } from "../ads/NativeToast";
import { useSubScreen } from "../ads/subScreen";
import { ProductMark, type ProductRef } from "../goal/FlowHead";
import { building, Line, listsBuilding, listsReady, sendLine } from "./ColdEmail";
import { daysText, SendingSheet, zoneName, ZoneSheet, type ChangeSending } from "./Sending";

/**
 * Getting ready on a phone and an iPad: how many steps are ready under the title, each step a group with one
 * status and a row per thing to do, and Approve or Launch at the bottom with the reason it waits. The emails
 * open as their own screen. The same loading and actions as the page on a computer.
 */

const city = (m: string) => m.split(",")[0].trim();
const usd = (cents: number) => `$${Math.round(cents / 100).toLocaleString("en-US")}`;
const pageOf = (url: string | undefined) => {
  try {
    if (!url) return "";
    const u = new URL(url);
    return `${u.host}${u.pathname.replace(/\/$/, "")}`;
  } catch {
    return "";
  }
};

interface Props {
  goalId: string;
  view: GoalView | null;
  loadError: string | null;
  ready: ReadyView | null;
  approved: boolean;
  product: ProductRef | null;
  conns: Connection[] | null;
  startDay: string | null;
  setReady: (v: ReadyView) => void;
  reload: () => Promise<void>;
  reloadGoal: () => Promise<void>;
  changeSending: ChangeSending;
  onDraftAgain: () => void;
  say: (m: string, ok?: boolean) => void;
  toast: ReactNode;
}

export function ReadyNative(p: Props) {
  const [params] = useSearchParams();
  const emails = params.get("show") === "emails";
  useSubScreen(emails ? "emails" : null);
  if (emails && p.ready && p.view) return <EmailsScreen {...p} ready={p.ready} />;
  return <ReadyScreen {...p} />;
}

/** A status pill for one step: ready (running, once the ads are on Meta), the agent at work, or waiting for the owner. */
function StepPill({ ok, agent, live }: { ok: boolean; agent?: boolean; live?: boolean }) {
  if (ok)
    return (
      <span className="pn-pill good">
        <i />
        {live ? "Running" : "Ready"}
      </span>
    );
  return agent ? (
    <span className="pn-pill violet">
      <i />
      The agent is on it
    </span>
  ) : (
    <span className="pn-pill warn">
      <i />
      Waiting for you
    </span>
  );
}

function Section({ name, ok, agent, live, children, note }: { name: string; ok: boolean; agent?: boolean; live?: boolean; children: ReactNode; note?: ReactNode }) {
  return (
    <section>
      <div className="pn-sh ad-shp">
        <h2>{name}</h2>
        <StepPill ok={ok} agent={agent} live={live} />
      </div>
      <div className="pn-grp">{children}</div>
      {note}
    </section>
  );
}

/** A connection's row: what it is, and Connected once Flashadz's checks pass, or what to fix. */
function CxRow({ title, ok, sub, done, to }: { title: string; ok: boolean; sub: string; done: string; to: string }) {
  if (ok)
    return (
      <div className="pn-row static">
        <span className="m">
          <span className="t">{title}</span>
          <span className="s">{sub}</span>
        </span>
        <span className="ad-ok">
          <Icon name="check" />
          {done}
        </span>
      </div>
    );
  return <PnRow to={to} title={title} sub={sub} wrap />;
}

function ReadyScreen({ goalId, view, loadError, ready, approved, product, conns, startDay, setReady, reload, reloadGoal, changeSending, onDraftAgain, say, toast }: Props) {
  const navigate = useNavigate();
  const [steps, setSteps] = useState<ReadyStep[] | null>(null);
  const [launch, setLaunch] = useState<LaunchView | null>(null);
  const [sheet, setSheet] = useState<"sending" | "postal" | "budget" | "undo" | "more" | null>(null);
  const [market, setMarket] = useState<string | null>(null);
  const [zoneFor, setZoneFor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const metaOn = !!ready?.metaOn;
  const emailOn = !!ready?.emailOn;

  const loadSteps = useCallback(async () => {
    if (!approved) return;
    const [s, l] = await Promise.all([ccApi.readySteps(goalId).catch(() => null), readyApi.launch(goalId).catch(() => null)]);
    if (s) setSteps(s.steps);
    if (l) setLaunch(l);
  }, [goalId, approved]);
  useEffect(() => {
    void loadSteps();
  }, [loadSteps, ready]);

  const back = { to: `/goals/${goalId}`, label: "Goal" };
  const more = (
    <button type="button" className="pn-tbtn" aria-label="More for this goal" onClick={() => setSheet("more")}>
      <Icon name="more" />
    </button>
  );
  const shell = (body: ReactNode) => (
    <div className="pn-screen ad-scr">
      <PhoneNav title="Getting ready" large back={back} />
      {body}
      <NativeToast>{toast}</NativeToast>
    </div>
  );
  if (!view) return shell(<p className="pn-empty">{loadError ?? "Loading Get ready…"}</p>);
  if (!approved)
    return shell(
      <div className="pn-empty">
        <b>This page opens once you approve a plan</b>
        <Link className="pn-btn pri ad-mt" to={`/goals/${goalId}`}>
          Go to the plan
        </Link>
      </div>,
    );
  if (!ready || !view.current) return shell(<p className="pn-empty">{loadError ?? "Loading Get ready…"}</p>);

  const config: GoalConfig = view.current.config;
  const settings = metaSettings(config.channels.meta?.settings);
  const step = (id: string) => steps?.find((s) => s.id === id);
  const allOk = (ids: string[]) => ids.every((id) => step(id)?.ok ?? true);
  const firstBad = (ids: string[]) => ids.map(step).find((s) => s && !s.ok);
  const meta = conns?.find((c) => c.kind === "meta");
  const email = conns?.find((c) => c.kind === "email");
  const run = async (f: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try {
      await f();
      if (ok) say(ok);
    } catch (e) {
      say(shownError(e), false);
    } finally {
      await reload();
      setBusy(false);
    }
  };

  // --- Each step, its status and its rows ---
  const sections: { id: string; name: string; ok: boolean; agent?: boolean; rows: ReactNode; note?: ReactNode }[] = [];
  const running = view.goal.campaign === "on";
  const cxRows: ReactNode[] = [];
  if (metaOn) {
    const m = metaOf(meta);
    const account = m.adAccounts.find((a) => a.id === m.adAccountId)?.name;
    const mOk = step("meta")?.ok ?? connectionReady(meta);
    cxRows.push(<CxRow key="meta" title="Meta" ok={mOk} done="Connected" to="/connections" sub={mOk ? (account ?? "Ad account") : (step("meta")?.detail ?? "Connect Meta")} />);
    const px = step("pixel");
    if (px || m.pixelName) cxRows.push(<CxRow key="pixel" title="Meta pixel" ok={px?.ok ?? true} done="Picked" to="/connections" sub={px && !px.ok ? (px.detail ?? px.label) : (m.pixelName ?? "Your pixel")} />);
    const cur = step("currency");
    if (cur && !cur.ok) cxRows.push(<CxRow key="cur" title="Ad account currency" ok={false} done="" to="/connections" sub={cur.detail ?? cur.label} />);
  }
  if (emailOn) {
    const eOk = connectionReady(email);
    const n = emailOf(email).mailboxIds.length;
    const sub = eOk ? `${n} sending address${n === 1 ? "" : "es"}` : shownState(email) === "checking" ? "Checking" : email ? hideProvider(failing(email.checks)[0]?.detail ?? "Fix it on Connections") : "Connect it first";
    cxRows.push(<CxRow key="email" title="Email sending" ok={eOk} done="Connected" to="/connections" sub={sub} />);
  }
  const cxOk = (!metaOn || allOk(["meta", "pixel", "currency"])) && (!emailOn || connectionReady(email));
  sections.push({ id: "cx", name: "Connections", ok: cxOk, agent: shownState(email) === "checking", rows: cxRows });

  if (metaOn) {
    const ads = ready.ads.filter((a) => a.status !== "launched");
    const live = ready.ads.filter((a) => a.status === "launched").length;
    const missing = ads.reduce((n, a) => n + a.missing, 0);
    const writing = ads.some((a) => a.status === "drafting") || drafting(ready.runs.ads);
    const where = firstBad(["link", "tracking", "learns"]);
    const adsSub = !ads.length && !live ? "None yet. 3 to 5 different ads work best." : [live ? `${live} running` : `${ads.length} ad${ads.length === 1 ? "" : "s"}`, missing ? `${missing} file${missing === 1 ? "" : "s"} to add` : "", writing ? "the agent is writing words" : ""].filter(Boolean).join(", ");
    sections.push({
      id: "ads",
      name: "Ads",
      ok: allOk(["link", "tracking", "learns", "ads", "formats"]),
      agent: writing && !missing,
      rows: (
        <>
          <PnRow to={`/goals/${goalId}/ads`} title="Where the ads open" sub={where ? (where.detail ?? where.label) : settings.opens === "metaform" ? "Meta's form" : pageOf(settings.link) || "Not set yet"} wrap />
          <PnRow to={`/goals/${goalId}/ads`} title="Your ads" sub={adsSub} wrap />
        </>
      ),
    });
    const a = settings.audience;
    const who = a ? `${andList(a.places.include.map(placeWords)) || "No place yet"}, aged ${a.ages.min} to ${a.ages.max >= 65 ? "65+" : a.ages.max}` : "Not chosen yet";
    const aud = step("audience");
    sections.push({
      id: "aud",
      name: "Audience",
      ok: aud?.ok ?? true,
      rows: <PnRow to={`/goals/${goalId}/audience`} title="Who sees the ads" sub={aud && !aud.ok && aud.detail ? aud.detail : who} wrap />,
    });
    const daily = settings.dailyCents ?? null;
    const d = step("daily");
    sections.push({
      id: "daily",
      name: "Daily budget",
      ok: d?.ok ?? !!daily,
      rows: (
        <PnRow
          onClick={() => setSheet("budget")}
          title="Daily budget"
          sub={d && !d.ok ? (d.detail ?? d.label) : `Up to ${usd(config.money.budgetCents)} in total`}
          value={daily ? `${usd(daily)} a day` : <span className="ad-vw">Set it</span>}
          chevron
          wrap
        />
      ),
    });
  }

  const mailParts = emailOn ? coldEmailRows() : null;
  if (mailParts) sections.push(mailParts);
  const done = sections.filter((s) => s.ok).length;

  function coldEmailRows() {
    const r = ready!;
    const approvedAll = r.emails.length > 0 && r.emails.every((e) => e.approved);
    const sendingOn = r.emails.some((e) => e.launched);
    const open = r.emailSends.length === 0;
    const agent = listsBuilding(r.lists) || drafting(r.runs.emails);
    const ok = listsReady(r.lists) && approvedAll && connectionReady(email);
    const s = r.sending;
    return {
      id: "mail",
      name: "Cold email",
      ok,
      agent,
      note: <p className="pn-note">The first line of each email is written for each person from their own page.</p>,
      rows: (
        <>
          {r.lists.length ? (
            r.lists.map((l) => (
              <PnRow key={l.market} onClick={() => setMarket(l.market)} title={l.market} sub={listSub(l)} value={listValue(l)} chevron wrap />
            ))
          ) : (
            <PnRow title="The list" sub="Add a market on the Channels step to start a list." />
          )}
          {s && (
            <PnRow
              onClick={open ? () => setSheet("sending") : undefined}
              title="Sending"
              sub={`${daysText(s.days)}, ${s.startHour} to ${s.endHour}, ${s.newPerDay} a day, ${s.minTimeBetweenEmails} min apart`}
              wrap
            />
          )}
          {r.emails.length ? (
            <PnRow to={`/goals/${goalId}/ready?show=emails`} title="The emails" sub={sendingOn ? "Sent to Smartlead" : approvedAll ? "Approved" : "Read them, then approve"} value={String(r.emails.length)} />
          ) : stopped(r.runs.emails) ? (
            <PnRow onClick={onDraftAgain} title="The emails" sub={hideProvider(`The agent stopped before writing them. ${r.runs.emails?.error ?? ""}`.trim())} value={<span className="ad-acc">Draft again</span>} chevron={false} wrap />
          ) : (
            <div className="pn-row static">
              <span className="m">
                <span className="t">The emails</span>
                <span className="s">{drafting(r.runs.emails) ? "The agent is writing them from the plan" : "The agent has not started on them yet"}</span>
              </span>
              {drafting(r.runs.emails) && <span className="ad-spin" aria-hidden="true" />}
            </div>
          )}
          <PnRow
            onClick={approvedAll ? undefined : () => setSheet("postal")}
            title="Postal address"
            sub={r.postalAddress || "For the footer of every email"}
            value={r.postalAddress ? undefined : <span className="ad-vw">Add</span>}
            chevron={!approvedAll}
            wrap
          />
        </>
      ),
    };
  }
  function listSub(l: ReadyList): string {
    const send = ready!.emailSends.find((x) => x.market === l.market);
    const line = send ? sendLine(send) : null;
    const zone = `Sends on ${zoneName(l.sendTimezone.timezone)} time`;
    if (l.state === "done") return [`${l.withEmail} people`, zone, line].filter(Boolean).join(" · ");
    if (building(l)) return `Finding people who published their own address.${l.people ? ` ${l.people} so far.` : ""}`;
    if (l.state === "not_started") return "The agent has not started this list yet.";
    return `The list run stopped before it finished.${l.people ? ` It found ${l.people} people.` : ""}`;
  }
  function listValue(l: ReadyList): ReactNode {
    const send = ready!.emailSends.find((x) => x.market === l.market);
    if (send?.campaignState === "on") return <span className="ad-okt">Sending</span>;
    if (l.state === "done") return <span className="ad-okt">Ready</span>;
    if (building(l)) return "Building";
    if (l.state === "not_started") return "Not started";
    return <span className="ad-vw">Stopped</span>;
  }

  // --- The bottom bar: Approve the emails, or Launch for a goal with Meta only ---
  let foot: ReactNode = null;
  if (emailOn) {
    const r = ready;
    const approvedAll = r.emails.length > 0 && r.emails.every((e) => e.approved);
    const sendingOn = r.emails.some((e) => e.launched);
    const why = !listsReady(r.lists) ? "Once the list is ready." : r.emails.length === 0 ? "Once the emails are written." : !r.postalAddress ? "Add the postal address first." : null;
    foot = approvedAll ? (
      <div className="pn-foot">
        <div className="ad-done-f">
          <span className="ad-ok">
            <Icon name="check" />
            {sendingOn ? "Sent to Smartlead" : "Approved"}
          </span>
          <span className="ad-done-t">{sendingOn ? "It sends each email on its date." : `The first emails go out ${startDay ?? "on their date"}.`}</span>
          {!sendingOn && (
            <button type="button" className="pn-btn sm" disabled={busy} onClick={() => setSheet("undo")}>
              Undo approval
            </button>
          )}
        </div>
      </div>
    ) : (
      <div className="pn-foot">
        {why && (
          <p className="ad-why">
            <Icon name="clock" />
            <span>{why}</span>
          </p>
        )}
        <button
          type="button"
          className={why ? "pn-btn pri wide ad-dis" : "pn-btn pri wide"}
          aria-disabled={!!why || busy}
          onClick={() => {
            if (busy) return;
            if (why) {
              if (!r.postalAddress && r.emails.length && listsReady(r.lists)) setSheet("postal");
              return;
            }
            void run(async () => setReady(await readyApi.approveEmails(goalId, Object.fromEntries(r.emails.map((e) => [String(e.step), e.rev])))), "Approved. Nothing is sent before its date.");
          }}
        >
          <Icon name="check" />
          Approve the first emails
        </button>
      </div>
    );
  } else if (metaOn && launch) {
    const ads = ready.ads.filter((a) => a.status === "ready");
    foot = (
      <LaunchFoot
        goalId={goalId}
        launch={launch}
        launchedAt={view.goal.launchedAt}
        busy={busy}
        onLaunch={() =>
          void run(async () => {
            await readyApi.launchNow(goalId, { version: view.goal.version, ads: Object.fromEntries(ads.map((a) => [a.id, a.rev])) });
            navigate(`/goals/${goalId}`);
          })
        }
        onTurnOn={() => void run(() => readyApi.turnOn(goalId)).then(loadSteps)}
      />
    );
  }

  const listOf = ready.lists.find((l) => l.market === market) ?? null;
  const sendOf = listOf ? ready.emailSends.find((x) => x.market === listOf.market) : undefined;
  const timezones = (config.channels.email?.settings as { timezones?: Record<string, string> } | undefined)?.timezones ?? {};

  return (
    <div className="pn-screen ad-scr">
      <PhoneNav title="Getting ready" large back={back} actions={more} sub={`${done} of ${sections.length} ready`} />
      <div className="pn-stack">
        <div className="pn-pad">
          <div className="ad-gref">
            {product && <ProductMark product={product} />}
            <span>{view.goal.sentence}</span>
          </div>
          <div className="ad-prog" aria-hidden="true">
            {sections.map((s) => (
              <i key={s.id} className={s.ok ? "ok" : ""} />
            ))}
          </div>
        </div>
        {sections.map((s) => (
          <Section key={s.id} name={s.name} ok={s.ok} agent={s.agent} live={running && ["ads", "aud", "daily"].includes(s.id)} note={s.note}>
            {s.rows}
          </Section>
        ))}
      </div>
      {foot}

      {ready.sending && <SendingSheet open={sheet === "sending"} onClose={() => setSheet(null)} sending={ready.sending} markets={ready.lists.map((l) => `${city(l.market)} on ${zoneName(l.sendTimezone.timezone)} time`).join(", ")} change={changeSending} />}
      <PostalSheet
        open={sheet === "postal"}
        onClose={() => setSheet(null)}
        saved={ready.postalAddress ?? ""}
        onSave={async (address) => {
          try {
            setReady(await readyApi.postalAddress(goalId, address));
            say("Saved.");
            return true;
          } catch (e) {
            say(shownError(e), false);
            return false;
          }
        }}
      />
      <Sheet open={market !== null && zoneFor === null} onClose={() => setMarket(null)} title={listOf?.market ?? ""}>
        {listOf && (
          <>
            <div className="pn-grp">
              <div className="pn-row static wrap">
                <span className="m">
                  <span className="t">{listOf.state === "done" ? `${listOf.withEmail} people` : building(listOf) ? "The agent is building this list" : listOf.state === "not_started" ? "Not started" : "Stopped"}</span>
                  <span className="s">{listSub(listOf)}</span>
                </span>
              </div>
              {sendOf && (
                <div className="pn-row static wrap">
                  <span className="m">
                    <span className="t">{sendOf.campaignState === "on" ? "Sending" : sendOf.campaignState === "paused" ? "Paused" : sendOf.campaignState === "ready" ? "Ready to send" : "Setting up"}</span>
                    {sendLine(sendOf) && <span className="s">{sendLine(sendOf)}</span>}
                  </span>
                </div>
              )}
              <PnRow
                onClick={sendOf ? undefined : () => setZoneFor(listOf.market)}
                title="Time zone"
                sub={listOf.sendTimezone.source === "goal" ? "The goal's own time zone" : listOf.sendTimezone.check ? "Part of this state keeps another time zone, so check it." : undefined}
                value={zoneName(listOf.sendTimezone.timezone)}
                chevron={!sendOf}
                wrap
              />
            </div>
            {!building(listOf) && listOf.state !== "done" && listOf.state !== "not_started" && (
              <div className="pn-grp ad-mt">
                <PnRow
                  className="accent"
                  title="Build the list again"
                  onClick={() => {
                    const m = listOf.market;
                    setMarket(null);
                    void run(() => readyApi.retryList(goalId, m), `Building the ${city(m)} list again.`);
                  }}
                  chevron={false}
                />
              </div>
            )}
            <p className="pn-note">Each person published their own address. The agent saved the page and the date for each.</p>
          </>
        )}
      </Sheet>
      <ZoneSheet
        market={zoneFor}
        tz={ready.lists.find((l) => l.market === zoneFor)?.sendTimezone.timezone ?? "America/New_York"}
        timezones={timezones}
        change={changeSending}
        onClose={() => setZoneFor(null)}
      />
      {metaOn && (
        <BudgetSheet
          open={sheet === "budget"}
          onClose={() => setSheet(null)}
          cents={settings.dailyCents ?? null}
          totalCents={config.money.budgetCents}
          maxCents={config.money.dailyMaxCents}
          onSave={async (cents) => {
            try {
              const next = { ...config, channels: { ...config.channels, meta: { ...config.channels.meta!, settings: { ...config.channels.meta!.settings, dailyCents: cents } } } };
              await ccApi.apply(goalId, view.current!.version, next, `Meta's daily budget: $${(cents / 100).toLocaleString("en-US")}`);
              await reloadGoal();
              await reload();
              say(`Daily budget is ${usd(cents)}.`);
              return true;
            } catch (e) {
              say(shownError(e), false);
              return false;
            }
          }}
        />
      )}
      <ActionSheet
        open={sheet === "undo"}
        onClose={() => setSheet(null)}
        title="Undo the approval?"
        message="Nothing is sent until you approve the emails again."
        actions={[{ label: "Undo approval", danger: true, onSelect: () => void run(async () => setReady(await readyApi.unapproveEmails(goalId)), "Approval taken back.") }]}
      />
      <ActionSheet
        open={sheet === "more"}
        onClose={() => setSheet(null)}
        actions={[
          { label: "The plan", onSelect: () => navigate(`/goals/${goalId}/plan`) },
          { label: "Change the goal", onSelect: () => navigate(`/goals/${goalId}/edit`) },
          { label: "Everything the agent did", onSelect: () => navigate(`/goals/${goalId}/log`) },
        ]}
      />
      <NativeToast>{toast}</NativeToast>
    </div>
  );
}

/** The postal address every email's footer carries, typed in a sheet. */
function PostalSheet({ open, onClose, saved, onSave }: { open: boolean; onClose: () => void; saved: string; onSave: (a: string) => Promise<boolean> }) {
  const [v, setV] = useState(saved);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setV(saved);
  }, [open, saved]);
  const save = async () => {
    if (!v.trim()) return;
    setBusy(true);
    const ok = await onSave(v.trim());
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Postal address"
      left={
        <button type="button" className="pn-tbtn" onClick={onClose}>
          Cancel
        </button>
      }
      right={
        <button type="button" className="pn-tbtn strong" disabled={busy || !v.trim()} onClick={() => void save()}>
          Save
        </button>
      }
    >
      <p className="ad-shs">It goes at the foot of every email, above the unsubscribe line.</p>
      <div className="pn-form">
        <input
          className="pn-inp"
          value={v}
          autoComplete="street-address"
          placeholder="Street, city, state and ZIP"
          aria-label="Business postal address, for the footer"
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void save()}
        />
      </div>
    </Sheet>
  );
}

// --- The cold emails, as their own screen ---

function MailCard({ m, footer }: { m: ReadyEmail; footer: ReactNode }) {
  return (
    <article className="ad-mail">
      <div className="ad-mh">
        <span className="pn-pill acc">Email {m.step}</span>
        {m.launched ? <span className="ad-okt">Sent to Smartlead</span> : <span className="ad-mw">{m.when}</span>}
      </div>
      <div className="ad-mrow">
        <span>To</span>
        <span className="rd-ph">first name</span>
      </div>
      <h3 className="ad-subj">{m.subject}</h3>
      <div className="ad-mb">
        {m.body.split("\n").map((l, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: an email's lines never move while shown.
          <Line key={i} text={l} />
        ))}
      </div>
      {footer}
    </article>
  );
}

function EmailsScreen({ goalId, ready, setReady, say, toast }: Props & { ready: ReadyView }) {
  const ipad = useRail();
  const [n, setN] = useState("1");
  const [editing, setEditing] = useState(false);
  const emails = ready.emails;
  const approved = emails.length > 0 && emails.every((e) => e.approved);
  const cur = emails.find((e) => String(e.step) === n) ?? emails[0];
  const addr = ready.postalAddress?.trim();
  const footer = (
    <div className="ad-mf">
      {addr ? addr : <span className="rd-ph">your postal address</span>}
      <br />
      <u>{ready.emailFooter?.unsubscribeText ?? "Unsubscribe"}</u>
    </div>
  );
  const markets = andList(ready.lists.map((l) => l.market));
  return (
    <div className="pn-screen ad-scr">
      <PhoneNav
        title="The emails"
        large
        back={{ to: `/goals/${goalId}/ready`, label: "Getting ready" }}
        sub={`Cold email${markets ? `, ${markets}` : ""}`}
        actions={
          !approved && cur ? (
            <button type="button" className="pn-tbtn strong" onClick={() => setEditing(true)}>
              Edit
            </button>
          ) : undefined
        }
      />
      {!emails.length ? (
        <p className="pn-empty">The agent has not written the emails yet.</p>
      ) : ipad ? (
        <div className="ad-two">
          {emails.map((m) => (
            <MailCard key={m.step} m={m} footer={footer} />
          ))}
        </div>
      ) : (
        <div className="pn-stack tight">
          <Segmented label="Which email" value={n} onChange={setN} options={emails.map((m) => ({ value: String(m.step), label: `Email ${m.step}` }))} />
          <div className="pn-pad">
            <MailCard m={cur} footer={footer} />
          </div>
          <p className="pn-note">The first line is written for each person from their own page.</p>
        </div>
      )}
      {cur && (
        <EditMail
          open={editing}
          onClose={() => setEditing(false)}
          email={cur}
          emails={emails}
          ipad={ipad}
          onSave={async (e) => {
            try {
              setReady(await readyApi.editEmail(goalId, e));
              say("Saved.");
              return true;
            } catch (err) {
              say(shownError(err), false);
              return false;
            }
          }}
        />
      )}
      <NativeToast>{toast}</NativeToast>
    </div>
  );
}

/** One email's subject and words in a sheet; words in braces are filled in for each person. */
function EditMail({ open, onClose, email, emails, ipad, onSave }: { open: boolean; onClose: () => void; email: ReadyEmail; emails: ReadyEmail[]; ipad: boolean; onSave: (e: ReadyEmail) => Promise<boolean> }) {
  const [step, setStep] = useState(email.step);
  const [d, setD] = useState({ subject: email.subject, body: email.body });
  const [busy, setBusy] = useState(false);
  const target = emails.find((e) => e.step === step) ?? email;
  useEffect(() => {
    if (!open) return;
    setStep(email.step);
    setD({ subject: email.subject, body: email.body });
  }, [open, email]);
  const pick = (s: number) => {
    const e = emails.find((x) => x.step === s);
    if (!e) return;
    setStep(s);
    setD({ subject: e.subject, body: e.body });
  };
  const save = async () => {
    const subject = d.subject.trim();
    const body = d.body.trim();
    if (!subject || !body) return;
    if (subject === target.subject && body === target.body) return onClose();
    setBusy(true);
    const ok = await onSave({ ...target, subject, body });
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Sheet
      open={open}
      onClose={onClose}
      tall
      title={`Email ${step}`}
      left={
        <button type="button" className="pn-tbtn" onClick={onClose}>
          Cancel
        </button>
      }
      right={
        <button type="button" className="pn-tbtn strong" disabled={busy || !d.subject.trim() || !d.body.trim()} onClick={() => void save()}>
          Save
        </button>
      }
    >
      {ipad && emails.length > 1 && (
        <div className="ad-mb12">
          <Segmented label="Which email" value={String(step)} onChange={(v) => pick(Number(v))} options={emails.map((m) => ({ value: String(m.step), label: `Email ${m.step}` }))} />
        </div>
      )}
      <div className="pn-form">
        <div className="pn-field">
          <label htmlFor="ad-ms">Subject</label>
          <input id="ad-ms" className="pn-inp" value={d.subject} onChange={(e) => setD({ ...d, subject: e.target.value })} />
        </div>
        <div className="pn-field">
          <label htmlFor="ad-mbody">Email</label>
          <textarea id="ad-mbody" className="pn-inp" rows={11} value={d.body} onChange={(e) => setD({ ...d, body: e.target.value })} />
        </div>
        <p className="ad-hint">Words in braces, like {"{first name}"}, are filled in for each person.</p>
      </div>
    </Sheet>
  );
}

